/**
 * Hero-showcase carousel frames.
 *
 * Feb-2026: the homepage hero surfaces three real catalog vials that
 * rotate as a COORDINATED trio every 5 seconds.  We NEVER show two
 * products from the same research family in the same frame (no two
 * CJC-family vials, no two GLP-family vials, etc.).
 *
 * Every slug below is a real, purchasable SKU that ships with an
 * existing /api/media/<slug>/... image.  Frames are ordered so
 * sequential frames swap all three slots (not a single-item shift) —
 * this reads as a full "product refresh" rather than a marquee.
 *
 * Rules enforced by `lib/__tests__/hero-showcase-frames.test.js`:
 *   * Every frame has EXACTLY 3 slugs.
 *   * Every slug in every frame has a mapped family key.
 *   * Family keys within a frame are UNIQUE.
 *   * No slug appears twice inside the same frame.
 *   * At least 3 frames · at least 9 distinct slugs overall.
 */

// Family key per slug — kept small on purpose (only slugs we actually
// use in a frame).  Adding a new slug to a frame requires adding it
// here first, or the tests fail.
export const HERO_SHOWCASE_FAMILY = Object.freeze({
  // Growth-hormone / performance
  "cjc-1295-no-dac-5-mg":     "growth",
  "cjc-1295-ipamorelin-5-5-mg": "growth",
  "ipamorelin-10-mg":         "growth",
  "hexarelin-5-mg":           "growth",

  // Recovery / repair
  "bpc-157-10-mg":            "recovery",
  "tb-500-10-mg":             "recovery",

  // Skin / copper complexes
  "ghk-cu-100-mg":            "skin",
  "ahk-cu-100-mg":            "skin",
  "ghk-cu-50-mg":             "skin",

  // Metabolic / GLP analogs
  "glp-1s-10-mg":             "metabolic",
  "cagrilintide-10-mg":       "metabolic",
  "aod-9604-10-mg":           "metabolic",

  // Longevity / bioregulators
  "epithalon-10-mg":          "longevity",
  "cardiogen-20-mg":          "longevity",
  "cartalax-20-mg":           "longevity",

  // Immune / thymic
  "thymosin-alpha-1-10-mg":   "immune",
  "kpv-10-mg":                "immune",

  // Cognitive / neuropeptides
  "pt-141-10-mg":             "cognitive",
  "dihexa-20-mg":             "cognitive",
});

// Four curated frames — each a trio with three distinct family keys.
// 12 unique products in rotation (well above the 9-minimum floor).
export const HERO_SHOWCASE_FRAMES = Object.freeze([
  // Growth · Recovery · Skin
  Object.freeze(["cjc-1295-no-dac-5-mg", "bpc-157-10-mg", "ghk-cu-100-mg"]),
  // Metabolic · Longevity · Immune
  Object.freeze(["glp-1s-10-mg", "epithalon-10-mg", "thymosin-alpha-1-10-mg"]),
  // Growth · Recovery · Cognitive
  Object.freeze(["ipamorelin-10-mg", "tb-500-10-mg", "pt-141-10-mg"]),
  // Skin · Metabolic · Longevity
  Object.freeze(["ahk-cu-100-mg", "cagrilintide-10-mg", "cardiogen-20-mg"]),
]);

/** Rotation cadence in milliseconds. */
export const HERO_ROTATION_MS = 5000;

/** Utility used by both the component and its tests. */
export function familyKeysForFrame(frame) {
  return frame.map((slug) => HERO_SHOWCASE_FAMILY[slug]);
}

/** Every distinct slug referenced across every frame. */
export function allShowcaseSlugs() {
  return Array.from(new Set(HERO_SHOWCASE_FRAMES.flat()));
}
